import React from 'react';
import * as XLSX from 'xlsx-js-style';
import { v4 as uuid } from 'uuid';
import { Button } from '@avila-tek/ui';
import { useNotify } from '../../../hooks';
import { ExcelDocument } from '../../../models';
import DropZone from './DropZone';
import { validateExcelDocument } from '../../../lib/formValidations';

interface ExcelFormProps {
  sheetName: string;
  updateData?: (data: ExcelDocument) => void;
  documentFormat?: string;
}

function ExcelForm({ sheetName, updateData, documentFormat }: ExcelFormProps) {
  const notify = useNotify();
  const [doc, setDoc] = React.useState<ExcelDocument>(null);

  const onDrop = React.useCallback(
    (acceptedFiles: File[]): void => {
      acceptedFiles
        .map((file: File) => {
          if (file.size > 1000000) {
            notify('Tu archivo debe pesar menos de 1MB', 'error');
            return null;
          }

          const reader = new FileReader();
          reader.onload = function onLoadFile(e) {
            const data = e.target.result;
            const workbook = XLSX.read(data, { type: 'binary' });

            const worksheet = workbook.Sheets[sheetName];
            const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet);

            setDoc({
              id: uuid(),
              tableRow: jsonData,
              name: file?.name ?? uuid(),
              file,
            });
          };
          reader.readAsBinaryString(file);
          return file;
        })
        .filter((x) => !!x) as File[];
    },
    [notify, setDoc, sheetName]
  );

  const downloadFormat = () => {
    const urlArchivo = documentFormat;
    window.open(urlArchivo, '_blank');
  };

  React.useEffect(() => {
    if (doc) {
      // Validate content of excel document
      const validate = validateExcelDocument(doc);
      if (validate) {
        notify(validate, 'error');
        setDoc(null);
        return;
      }
      updateData(doc);
      setTimeout(() => {
        setDoc(null);
      }, 3000);
    }
  }, [doc]);

  return (
    <section className="relative overflow-hidden">
      {documentFormat ? (
        <div className="flex flex-col justify-end items-end px-10 pb-5">
          <Button
            className="w-[50%] sm:w-[40%]  lg:w-[30%] text-nowrap text-white"
            onClick={downloadFormat}
          >
            Descargar formato
          </Button>
          <span className="p-0 text-[9px] text-gray-300">
            La informacion debe ser ingresada en el formato descargado
          </span>
        </div>
      ) : null}
      <DropZone
        onDrop={onDrop}
        className="w-full h-auto px-2 py-5 z-10"
        text="Arrastre el archivo de excel o haga click acá para buscarlo"
        accept={{
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
            '.xlsx',
          ],
          'application/vnd.ms-excel': ['.xls'],
        }}
      />
      {doc ? (
        <div className=" absolute top-1 right-2 z-20 max-w-[55%] text-nowrap ">
          <p className="text-sm text-gray-600">Archivo cargado: {doc.name}</p>
        </div>
      ) : null}
    </section>
  );
}

export default ExcelForm;
